/**
 * WebSocket Client Service
 * Handles WebSocket connections for real-time collaboration
 */

export interface WebSocketMessage {
  type: string;
  workspace_id: string;
  [key: string]: unknown;
}

export type MessageHandler = (message: WebSocketMessage) => void;
export type CloseHandler = () => void;
export type ErrorHandler = (error: Event) => void;

class WebSocketClient {
  private workspaceId: string;
  private accessToken: string;
  private socket: WebSocket | null = null;
  private messageHandlers: Set<MessageHandler> = new Set();
  private closeHandlers: Set<CloseHandler> = new Set();
  private errorHandlers: Set<ErrorHandler> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private reconnectTimer: NodeJS.Timeout | null = null;
  private isManualClose = false;

  constructor(workspaceId: string, accessToken: string) {
    this.workspaceId = workspaceId;
    this.accessToken = accessToken;
    this.connect();
  }

  /**
   * Connect to WebSocket server
   */
  private connect(): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    // Construct WebSocket URL:
    // - If VITE_WS_BASE_URL is set, use it
    // - Otherwise, use relative URL (ws:// or wss:// based on current protocol + host)
    let wsBaseUrl: string;
    if (import.meta.env.VITE_WS_BASE_URL) {
      wsBaseUrl = import.meta.env.VITE_WS_BASE_URL;
    } else if (typeof window !== 'undefined') {
      // Use relative URL - will be proxied by Nginx in Docker
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsBaseUrl = `${protocol}//${window.location.host}`;
    } else {
      wsBaseUrl = 'ws://localhost:8081';
    }
    const wsUrl = `${wsBaseUrl}/api/v1/ws/${this.workspaceId}?token=${encodeURIComponent(this.accessToken)}`;

    try {
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        console.log(`WebSocket connected to workspace ${this.workspaceId}`);
      };

      this.socket.onmessage = (event: MessageEvent) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.messageHandlers.forEach((handler) => handler(message));
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.socket.onclose = (event: CloseEvent) => {
        console.log(`WebSocket closed: ${event.code} ${event.reason}`);
        this.closeHandlers.forEach((handler) => handler());

        // Attempt to reconnect if not manually closed
        if (!this.isManualClose && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };

      this.socket.onerror = (error: Event) => {
        console.error('WebSocket error:', error);
        this.errorHandlers.forEach((handler) => handler(error));
        // Close socket on error to trigger reconnection logic
        if (this.socket) {
          this.socket.close();
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect();
      }
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000); // Max 30 seconds

    console.log(`Scheduling WebSocket reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * Send a message to the WebSocket server
   */
  send(message: WebSocketMessage): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket is not connected. Message not sent:', message);
      return;
    }

    try {
      this.socket.send(JSON.stringify(message));
    } catch (error) {
      console.error('Failed to send WebSocket message:', error);
    }
  }

  /**
   * Register a message handler
   */
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  /**
   * Register a close handler
   */
  onClose(handler: CloseHandler): () => void {
    this.closeHandlers.add(handler);
    return () => {
      this.closeHandlers.delete(handler);
    };
  }

  /**
   * Register an error handler
   */
  onError(handler: ErrorHandler): () => void {
    this.errorHandlers.add(handler);
    return () => {
      this.errorHandlers.delete(handler);
    };
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  /**
   * Get workspace ID
   */
  getWorkspaceId(): string {
    return this.workspaceId;
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.isManualClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.messageHandlers.clear();
    this.closeHandlers.clear();
    this.errorHandlers.clear();
  }
}

export { WebSocketClient };

